import type { ErrorResponse } from './types';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | undefined;
  readonly requestId: string | undefined;
  readonly retryAfter: number | undefined;

  constructor(status: number, body: Partial<ErrorResponse> | undefined, retryAfter?: number) {
    super(body?.message ?? `Request failed (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.code ?? (status === 0 ? 'network_error' : 'http_error');
    this.details = body?.details;
    this.requestId = body?.request_id;
    this.retryAfter = retryAfter;
  }

  static async fromResponse(res: Response, parsed?: unknown): Promise<ApiError> {
    let body: Partial<ErrorResponse> | undefined =
      parsed && typeof parsed === 'object' ? (parsed as Partial<ErrorResponse>) : undefined;
    if (!body) {
      try {
        body = (await res.clone().json()) as Partial<ErrorResponse>;
      } catch {
        body = undefined;
      }
    }
    const ra = res.headers.get('Retry-After');
    return new ApiError(res.status, body, ra ? Number(ra) : undefined);
  }

  static network(err: unknown): ApiError {
    return new ApiError(0, { code: 'network_error', message: err instanceof Error ? err.message : 'Network error' });
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}
